import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share, Plus, X, Smartphone, MoreVertical } from 'lucide-react';

// iter267 · Shows a small "Add to Home Screen" badge once per browser
// session, ~1 minute after opening the site (never when already running
// as an installed app). Tapping it slides up the existing step-by-step
// install sheet. Goal: nudge visitors toward the app-like experience.
const SESSION_KEY = 'dl_pwa_badge_done_v1';
const INSTALL_TRACKED_KEY = 'dl_pwa_install_tracked_v1';
const APPEAR_AFTER_MS = 60000;

const platformOf = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

const trackPwa = (event) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pwa/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, platform: platformOf() }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
};

const PWAInstallPrompt = () => {
  const [badge, setBadge] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredEvt, setDeferredEvt] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Native Capacitor shell — this IS the installed app; never pitch
    // "Add to Home Screen" inside it.
    if (window.Capacitor?.isNativePlatform?.()) return undefined;
    // Skip if already running as a PWA
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      // First launch from the Home Screen = a completed install (this is
      // the only signal iOS gives us). Count it exactly once.
      try {
        if (!localStorage.getItem(INSTALL_TRACKED_KEY)) {
          localStorage.setItem(INSTALL_TRACKED_KEY, '1');
          trackPwa('install');
        }
      } catch { /* ignore */ }
      return undefined;
    }
    // Android fires `appinstalled` right in the browser tab.
    const onInstalled = () => {
      try {
        if (!localStorage.getItem(INSTALL_TRACKED_KEY)) {
          localStorage.setItem(INSTALL_TRACKED_KEY, '1');
          trackPwa('install');
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('appinstalled', onInstalled);
    // Once per session — dismissing keeps it away until the next visit
    try {
      if (sessionStorage.getItem(SESSION_KEY)) {
        return () => window.removeEventListener('appinstalled', onInstalled);
      }
    } catch { /* ignore */ }

    const ua = window.navigator.userAgent;
    setIsIOS(/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua));

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    const showTimer = setTimeout(() => setBadge(true), APPEAR_AFTER_MS);
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      clearTimeout(showTimer);
    };
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setBadge(false);
    setSheet(false);
  };

  const nativeInstall = async () => {
    if (!deferredEvt) return;
    try {
      deferredEvt.prompt();
      await deferredEvt.userChoice;
      setDeferredEvt(null);
    } catch { /* ignore */ }
    dismiss();
  };

  if (typeof document === 'undefined' || (!badge && !sheet)) return null;
  // iter345 · Belt-and-suspenders native guard — the useEffect early-
  // return should already have prevented state from populating, but
  // in case the Capacitor bridge boots after mount OR the UA route
  // slipped past, catch it at render time too. This banner is a
  // web-only artifact; the native app IS the install destination.
  if (typeof window !== 'undefined') {
    const nativeByBridge = !!window.Capacitor?.isNativePlatform?.();
    const nativeByPlatform = window.Capacitor?.getPlatform?.() !== 'web' && window.Capacitor?.getPlatform?.() != null;
    if (nativeByBridge || nativeByPlatform) return null;
  }

  const badgePill = badge && !sheet && (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[95]"
      style={{ bottom: 'calc(96px + env(safe-area-inset-bottom))', animation: 'fadeSlideUp 360ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      data-testid="pwa-install-badge"
    >
      <div className="flex items-center gap-1 rounded-full bg-black/90 backdrop-blur-md ring-1 ring-blue-400/40 shadow-[0_12px_35px_-8px_rgba(59,130,246,0.45)] pl-3 pr-1.5 py-1.5">
        <button
          type="button"
          onClick={() => { setSheet(true); trackPwa('sheet_open'); }}
          className="flex items-center gap-2 text-white"
          data-testid="pwa-install-badge-open"
        >
          <span className="inline-flex w-6 h-6 rounded-full bg-blue-500/25 items-center justify-center">
            <Smartphone className="w-3.5 h-3.5 text-blue-300" />
          </span>
          <span className="text-[11px] font-bold tracking-wide whitespace-nowrap">
            Add Flythouse to your Home Screen
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
          data-testid="pwa-install-badge-close"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  const sheetEl = sheet && (
    <div className="fixed inset-0 z-[96]" data-testid="pwa-install-sheet" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div
        className="absolute bottom-0 left-0 right-0 rounded-t-[28px] bg-[#0b0b0d] ring-1 ring-white/10 px-5 pt-5 text-white max-w-[480px] mx-auto"
        style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom))', animation: 'fadeSlideUp 320ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-white/20 mb-4" />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 transition"
          data-testid="pwa-install-sheet-close"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="font-italic text-[10px] uppercase tracking-widest text-blue-300 mb-1.5">Flythouse · App</p>
        <p className="font-display text-2xl uppercase font-bold leading-[1.05] mb-2">
          Make it feel like an app
        </p>
        <p className="text-[13px] text-gray-300 mb-5 leading-snug">
          Add Flythouse to your Home Screen — full-screen, one tap away, with lock-screen pushes when a new story drops.
        </p>
        {isIOS ? (
          <ol className="text-[13px] text-gray-200 space-y-3 mb-2 leading-relaxed">
            <li className="flex items-center gap-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-white/10 items-center justify-center text-[11px] font-bold shrink-0">1</span>
              <span>Tap <Share className="w-4 h-4 inline-block mx-0.5 text-blue-300" /> Share in Safari's toolbar</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-white/10 items-center justify-center text-[11px] font-bold shrink-0">2</span>
              <span>Scroll and tap <span className="font-bold text-white">Add to Home Screen</span> <Plus className="w-4 h-4 inline-block text-blue-300" /></span>
            </li>
            <li className="flex items-center gap-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-white/10 items-center justify-center text-[11px] font-bold shrink-0">3</span>
              <span>Tap <span className="font-bold text-white">Add</span>, then open Flythouse from your Home Screen</span>
            </li>
          </ol>
        ) : deferredEvt ? (
          <button
            type="button"
            onClick={nativeInstall}
            className="w-full py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-gray-200 transition"
            data-testid="pwa-native-install-btn"
          >
            Install Flythouse
          </button>
        ) : (
          <ol className="text-[13px] text-gray-200 space-y-3 mb-2 leading-relaxed">
            <li className="flex items-center gap-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-white/10 items-center justify-center text-[11px] font-bold shrink-0">1</span>
              <span>Tap the <MoreVertical className="w-4 h-4 inline-block text-blue-300" /> menu in your browser</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-white/10 items-center justify-center text-[11px] font-bold shrink-0">2</span>
              <span>Tap <span className="font-bold text-white">Add to Home screen</span> and confirm</span>
            </li>
          </ol>
        )}
      </div>
    </div>
  );

  return createPortal(
    <>
      {badgePill}
      {sheetEl}
    </>,
    document.body,
  );
};

export default PWAInstallPrompt;
